import { useCallback, useState } from 'react';

/** A redirect to brewauth's sign-in: `login` or `loginFresh` from reauthClient. */
export type SignInRedirect = () => Promise<void>;

/** What the signed-out panels say when the sign-in redirect could not start. */
export const SIGN_IN_UNREACHABLE_MESSAGE = "We can't reach the sign-in service right now.";

/**
 * Sign-in redirects whose failure the user sees (#2371).
 *
 * signinRedirect reads brewauth's OIDC metadata before it navigates, so when
 * brewauth cannot be reached it rejects and the page stays put: an automatic
 * login left its spinner up for good, and a Sign in button did nothing.
 * `unreachable` turns true once a redirect fails; `retry` runs that same
 * redirect again. A redirect that succeeds leaves the page, so nothing clears
 * the flag.
 */
export function useSignInRedirect() {
  const [failed, setFailed] = useState<SignInRedirect | null>(null);

  /** Record a redirect that failed elsewhere (autoLogin starts its own). */
  const report = useCallback((redirect: SignInRedirect) => setFailed(() => redirect), []);

  const signIn = useCallback(
    (redirect: SignInRedirect) => {
      // fire-and-forget: a success navigates away; a failure is reported through `unreachable`
      void redirect().catch(() => report(redirect));
    },
    [report],
  );

  const retry = useCallback(() => {
    if (failed) signIn(failed);
  }, [failed, signIn]);

  return { unreachable: failed !== null, signIn, retry, report };
}
